import { ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { useConnectionState, ZeroProvider } from "@rocicorp/zero/react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  appMutators,
  appSchema,
  type AppZeroContext,
} from "@production-hub/shared";
import type { CustomMutatorDefs, UpdateNeededReason } from "@rocicorp/zero";
import { useAuth } from "@/hooks/useAuth";
import { useAuthToken } from "@/hooks/useAuthToken";
import { notifyNewVersionAvailable } from "@/hooks/useNewVersionCheck";
import { Button } from "@/components/ui/button";

const zeroCacheURL = import.meta.env.VITE_ZERO_CACHE_URL as string | undefined;
const zeroQueryURL =
  (import.meta.env.VITE_ZERO_QUERY_URL as string | undefined) ??
  "/api/zero/query";
const zeroMutateURL =
  (import.meta.env.VITE_ZERO_MUTATE_URL as string | undefined) ??
  "/api/zero/mutate";

// Minimum gap between needs-auth–triggered force-refreshes, to avoid a
// tight loop if the server keeps rejecting the refreshed token.
const NEEDS_AUTH_REFRESH_COOLDOWN_MS = 5_000;

// Zero's default reloads the page after a delay. While that reload is pending,
// any connect() call makes Zero's run loop spin and freeze the tab, so we show
// the new-version banner instead and let the user reload.
function handleUpdateNeeded(reason: UpdateNeededReason) {
  console.warn("[zero] update needed", reason);
  notifyNewVersionAvailable();
}

function ZeroStartupLoading({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <div className="text-center">
          <p className="text-sm font-medium">{message}</p>
          <p className="text-xs text-muted-foreground">
            Local data will appear as soon as sync is ready.
          </p>
        </div>
      </div>
    </div>
  );
}

function ZeroConfigurationError() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold mb-2">Sync is not configured</h1>
        <p className="text-sm text-muted-foreground">
          The app was built without VITE_ZERO_CACHE_URL. Rebuild the dev
          environment so Zero-backed screens can load.
        </p>
      </div>
    </div>
  );
}

/**
 * Handles the connection states where Zero waits for the app to act:
 * `needs-auth` (refresh the token; ZeroProvider reconnects with the new auth
 * prop) and `error` (offer a reload, which recovers from every error kind,
 * including an outdated client schema after a deploy).
 */
function ZeroConnectionHandler({
  children,
  onNeedsAuth,
}: {
  children: ReactNode;
  onNeedsAuth: () => void;
}) {
  const connectionState = useConnectionState();
  const lastTriggeredRef = useRef(0);

  useEffect(() => {
    if (connectionState.name !== "needs-auth") return;
    const now = Date.now();
    if (now - lastTriggeredRef.current < NEEDS_AUTH_REFRESH_COOLDOWN_MS) return;
    lastTriggeredRef.current = now;
    onNeedsAuth();
  }, [connectionState.name, onNeedsAuth]);

  return (
    <>
      {children}
      {connectionState.name === "error" && (
        <div className="fixed top-4 left-1/2 z-50 -translate-x-1/2">
          <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-background px-4 py-3 shadow-lg">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <span className="text-sm">Sync stopped.</span>
            <Button size="sm" onClick={() => window.location.reload()}>
              Reload
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export function AppZeroProvider({ children }: { children: ReactNode }) {
  const { user, role, personId, isLoading } = useAuth();
  const { token, forceRefresh } = useAuthToken();

  useEffect(() => {
    if (!zeroCacheURL && import.meta.env.DEV) {
      console.warn(
        "VITE_ZERO_CACHE_URL is not configured; app Zero sync is disabled.",
      );
    }
  }, []);

  const context: AppZeroContext | null = useMemo(
    () =>
      user?.id
        ? {
            userID: user.id,
            personID: personId,
            role,
            // The server filters synced rows by project collaboration.
            clientSide: true,
          }
        : null,
    [personId, role, user?.id],
  );

  const handleNeedsAuth = useCallback(() => {
    void forceRefresh();
  }, [forceRefresh]);
  const zeroAuth = useMemo(() => (token ? `Bearer ${token}` : null), [token]);

  useEffect(() => {
    if (!user?.id || token) return;
    void forceRefresh();
  }, [forceRefresh, token, user?.id]);

  if (isLoading) return <ZeroStartupLoading message="Restoring session" />;
  if (!user?.id || !context) return <>{children}</>;
  if (!zeroCacheURL) return <ZeroConfigurationError />;
  if (!zeroAuth) return <ZeroStartupLoading message="Preparing sync" />;

  return (
    <ZeroProvider
      key={user.id}
      userID={user.id}
      auth={zeroAuth}
      schema={appSchema}
      mutators={appMutators as unknown as CustomMutatorDefs}
      cacheURL={zeroCacheURL}
      queryURL={zeroQueryURL}
      mutateURL={zeroMutateURL}
      context={context}
      onUpdateNeeded={handleUpdateNeeded}
    >
      <ZeroConnectionHandler onNeedsAuth={handleNeedsAuth}>
        {children}
      </ZeroConnectionHandler>
    </ZeroProvider>
  );
}
